import { useCallback, useEffect, useState } from 'react';
import { userPreferencesService } from '@/services/userPreferencesService';
import type { HomeWidgetsPreference } from '@/types/services';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('useHomeWidgetsPreference', 'home');
const MAX_REMOVED_WIDGETS = 100;
const MAX_WIDGET_KEY_LENGTH = 100;
const WITHHELD_HOME_WIDGET_KEYS = new Set(['Market Watch']);

export const EMPTY_HOME_WIDGETS_PREFERENCE: HomeWidgetsPreference = {
  removed: []
};

export function normalizeHomeWidgetsPreference(value: unknown): HomeWidgetsPreference {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return EMPTY_HOME_WIDGETS_PREFERENCE;
  }

  const removed = (value as { removed?: unknown }).removed;
  if (!Array.isArray(removed) || removed.length > MAX_REMOVED_WIDGETS) {
    return EMPTY_HOME_WIDGETS_PREFERENCE;
  }

  const normalized: string[] = [];
  const seen = new Set<string>();

  for (const key of removed) {
    if (typeof key !== 'string') {
      return EMPTY_HOME_WIDGETS_PREFERENCE;
    }

    const trimmed = key.trim();
    if (!trimmed || trimmed.length > MAX_WIDGET_KEY_LENGTH) {
      return EMPTY_HOME_WIDGETS_PREFERENCE;
    }

    if (WITHHELD_HOME_WIDGET_KEYS.has(trimmed)) {
      continue;
    }

    if (!seen.has(trimmed)) {
      seen.add(trimmed);
      normalized.push(trimmed);
    }
  }

  return { removed: normalized };
}

export function useHomeWidgetsPreference(): {
  removed: readonly string[];
  isResolved: boolean;
  isWritable: boolean;
  setRemoved: (removed: string[]) => void;
} {
  const [removed, setRemovedState] = useState<readonly string[]>([]);
  const [isResolved, setIsResolved] = useState(false);
  const [isWritable, setIsWritable] = useState(false);

  useEffect(() => {
    let isActive = true;

    const hydrate = async () => {
      try {
        const result = await userPreferencesService.getPreferences();
        if (!isActive) {
          return;
        }

        if (!result.success) {
          log.warn('Failed to load Home widget preferences', {
            metadata: { error: result.error.message }
          });
          setRemovedState([]);
          return;
        }

        setIsWritable(true);
        setRemovedState(
          normalizeHomeWidgetsPreference(result.data.uiPreferences.homeWidgets).removed
        );
      } catch (error) {
        if (!isActive) {
          return;
        }
        log.warn(
          'Unexpected error loading Home widget preferences',
          undefined,
          error as Error
        );
        setRemovedState([]);
      } finally {
        if (isActive) {
          setIsResolved(true);
        }
      }
    };

    void hydrate();

    return () => {
      isActive = false;
    };
  }, []);

  const setRemoved = useCallback((nextRemoved: string[]) => {
    if (!isWritable) {
      return;
    }

    const normalized = normalizeHomeWidgetsPreference({ removed: nextRemoved });
    setRemovedState(normalized.removed);

    void userPreferencesService
      .updateUIPreferences({ homeWidgets: normalized })
      .then((result) => {
        if (!result.success) {
          log.warn('Failed to persist Home widget preferences', {
            metadata: { error: result.error.message }
          });
        }
      })
      .catch((error) => {
        log.warn(
          'Unexpected error persisting Home widget preferences',
          undefined,
          error as Error
        );
      });
  }, [isWritable]);

  return {
    removed,
    isResolved,
    isWritable,
    setRemoved
  };
}
